// src/services/InvoiceService.js
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { sendEmail } from "../emailService";

export async function generateAndSendInvoice(details, items, price) {
  // Destructure overall price values: total, overall subtotal, discount
  const [total, totalPrice, discount] = price;
  toast.success("Generating Invoice...");

  // Generate a unique invoice number
  const invoiceNum = `INV-${Math.floor(Math.random() * 100000)}`;

  // Safeguard the details object with defaults
  const safeDetails = {
    name: details.name || "Customer Name",
    email: details.email || "example@example.com",
    address: details.address || "Customer Address",
    country: details.country || "Customer Country",
    state: details.state || "Customer State",
    zip: details.zip || "000000",
    phoneNumber: details.phoneNumber || "+000 000 0000",
    carModel: details.model || "Unknown Model",
    carYear: details.year || "Unknown Year",
    currentKm: details.currentKm || "10,000 km",
    vinNumber: details.vinNumber || "1HGBH41JXMN109186",
    accidentOrWrittenOff: details.accidentOrWrittenOff || "No",
    color: details.color || "Black",
  };

  // Safeguard cart items with defaults and include needed fields
  const safeCartItems = items.map((item) => ({
    name: item.headTitle || `${safeDetails.carModel} (${safeDetails.carYear})`,
    description: item.productDescription || "No description available",
    productId: item.productId || "P98A15",
    totalPrice: Number((item.activePlan ? item.activePlan.price : item.price).toString().replace(/,/g, '')) || 0,
  }));
  

  // Initialize jsPDF
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth(); // approx. 210 mm for A4
  const pageHeight = doc.internal.pageSize.getHeight(); // approx. 297 mm for A4
  const margin = 15;

  // 1) TOP SECTION: COMPANY INFO & INVOICE DETAILS
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(0, 45, 88);
  doc.text("MOTOR EASY", margin, 15);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Your One-Stop Destination for Buying & Selling Motors with Ease!", margin, 20);

  // Invoice details on the right
  const rightX = pageWidth - margin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`INVOICE #: ${invoiceNum}`, rightX, 15, { align: "right" });

  const currentDate = new Date().toLocaleDateString();
  doc.text(`DATE: ${currentDate}`, rightX, 20, { align: "right" });

  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 7);
  const dueDateStr = dueDate.toLocaleDateString();
  doc.text(`DUE: ${dueDateStr}`, rightX, 25, { align: "right" });

  // Draw a blue horizontal line under the top section
  doc.setDrawColor(0, 102, 204);
  doc.line(margin, 30, pageWidth - margin, 30);

  let currentY = 35; // Starting Y position for the next section

  // 2) CUSTOMER & VEHICLE INFO TABLE
  const customerInfo = [
    `Name: ${safeDetails.name}`,
    `Address: ${safeDetails.address}`,
    `City/State/ZIP: ${safeDetails.country}, ${safeDetails.state}, ${safeDetails.zip}`,
    `Phone: ${safeDetails.phoneNumber}`,
    `Email: ${safeDetails.email}`,
  ].join("\n");

  const vehicleInfo = [
    `Make: ${details.make || "N/A"}`,
    `Model: ${safeDetails.carModel}`,
    `Year: ${safeDetails.carYear}`,
    `Color: ${safeDetails.color}`,
    `VIN #: ${safeDetails.vinNumber}`,
    `Reg #: ${details.carNumber || "N/A"}`,
    `Mileage: ${safeDetails.currentKm}`,
  ].join("\n");

  doc.autoTable({
    startY: currentY,
    head: [["CUSTOMER INFO", "VEHICLE INFO"]],
    body: [[customerInfo, vehicleInfo]],
    theme: "plain",
    headStyles: {
      fillColor: [0, 45, 88],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "start",
    },
    bodyStyles: {
      fillColor: [219, 229, 239],
      textColor: [0, 0, 0],
    },
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 4,
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: "50%" },
      1: { cellWidth: "50%" },
    },
    margin: { left: margin, right: margin },
    tableWidth: "auto",
  });

  currentY = doc.lastAutoTable.finalY + 5;

  // 3) INVOICE ITEMS TABLE
  const invoiceTableBody = safeCartItems.map((item) => [
    item.name,
    item.description,
    item.productId,
    `$${item.totalPrice}`,
  ]);

  // Append extra empty rows if needed for visual balance
  for (let i = 0; i < 8; i++) {
    invoiceTableBody.push(["", "", "", ""]);
  }

  doc.autoTable({
    startY: currentY,
    head: [["PART NAME", "Product Description", "Product ID", "SUBTOTAL"]],
    body: invoiceTableBody,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      lineColor: [150, 150, 150],
      lineWidth: 0.1,
      cellPadding: 4,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [0, 45, 88],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    margin: { left: margin, right: margin },
    tableWidth: "50%",
  });

  let afterTableY = doc.lastAutoTable.finalY + 5;

  // 4) SUBTOTAL, TAX, DISCOUNT, TOTAL SUMMARY
  doc.setTextColor(27, 183, 255);
  const summaryLineSpacing = 6;
  const summaryX = pageWidth - margin - 50;
  let summaryY = afterTableY;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("SUBTOTAL:", summaryX, summaryY, { align: "right" });
  doc.text(`$${totalPrice.toFixed(2)}`, summaryX + 50, summaryY, { align: "right" });
  summaryY += summaryLineSpacing;

  const taxAmount = totalPrice * 0.1;
  doc.text("TAX (10%):", summaryX, summaryY, { align: "right" });
  doc.text(`$${taxAmount.toFixed(2)}`, summaryX + 50, summaryY, { align: "right" });
  summaryY += summaryLineSpacing;

  doc.text("DISCOUNT:", summaryX, summaryY, { align: "right" });
  doc.text(`-$${discount.toFixed(2)}`, summaryX + 50, summaryY, { align: "right" });
  summaryY += summaryLineSpacing;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  summaryY += 3;
  doc.text("TOTAL:", summaryX, summaryY, { align: "right" });
  doc.text(`$${total.toFixed(2)}`, summaryX + 50, summaryY, { align: "right" });
  summaryY += summaryLineSpacing;

  // Reset text color for notes
  doc.setTextColor(0, 0, 0);

  // 5) NOTES / THANK YOU MESSAGE
  const noteY = summaryY + 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Please include the invoice number on your checks.", margin, noteY);
  doc.text("Thank you for your business!", margin, noteY + 5);

  // 6) FOOTER (CONTACT INFO)
  const bottomMargin = 10;
  const footerHeight = 20;
  const footerY = pageHeight - bottomMargin - footerHeight;
  
  doc.setFillColor(0, 45, 88);
  doc.rect(margin, footerY, pageWidth - 2 * margin, footerHeight, "F");

  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(
    "Phone: 1300 221 480 Email: Info@Motoreasy.com.au",
    pageWidth / 2,
    footerY + 8,
    { align: "center" }
  );
  doc.text(
    "123 Motor St, Car   Website: https://motoreasy.com.au",
    pageWidth / 2,
    footerY + 16,
    { align: "center" }
  );

  // Optionally, generate a PDF data URL if you need to attach it in the email
  // const pdfOutput = doc.output("datauristring");

  // Build invoice data to be saved in Firestore
  const pdfBase64 = btoa(doc.output());
  const invoiceData = {
    details: safeDetails,
    cartItems: safeCartItems,
    totalPrice: totalPrice || 0,
    discount: discount || 0,
    total: total || 0,
    pdfBase64,
    date: new Date().toLocaleDateString(),
  };
doc.save('invoice.pdf')
  // Save invoiceData to Firestore and send email
  try {
    const docRef = await addDoc(collection(db, "Orders"), invoiceData);
    toast.success("Please check email for invoice");
    console.log("Order Document written with ID: ", docRef.id);
    await sendEmail(invoiceData, safeDetails);
    return invoiceData;
  } catch (error) {
    console.error("Error saving invoice to Firestore:", error);
    throw error;
  }
}
